import TopNavigation, { type TopNavigationProps } from '@cloudscape-design/components/top-navigation';
import { useLangSwitcherUtility, useT } from '@vextura/i18n';
import { type ReactNode } from 'react';
import { useAuth } from './auth/AuthProvider.js';
import { useScope } from './scope/ScopeProvider.js';

export interface TopNavProps {
  productName?: string;
  /** Legacy: open a modal command palette. Superseded by `search` slot but
   *  still accepted so existing callers don't break. */
  onSearch?: () => void;
  /** Inline search input rendered in TopNavigation's `search` slot (AWS
   *  Console style — dropdown attached to the input, no modal). When set,
   *  the icon-button trigger is suppressed. */
  search?: ReactNode;
  onToggleTheme?: () => void;
  onToggleDensity?: () => void;
  theme?: 'light' | 'dark';
}

export function TopNav({
  productName,
  onSearch,
  search,
  onToggleTheme,
  onToggleDensity,
  theme = 'light',
}: TopNavProps) {
  const auth = useAuth();
  const scope = useScope();
  const t = useT('shell');
  const resolvedProductName = productName ?? t('productName');
  const langUtility = useLangSwitcherUtility(t('topNav.language'));

  // Tenant identity is auth-derived and shown read-only in the product
  // title. There is NO tenant picker — cross-tenant access happens via
  // role assumption (see AssumeRoleButton).
  //
  // Region is shown alongside tenant (multi-region P1/P8) so the customer
  // always sees the exact (tenant, region) tuple every SDK call carries.
  // Without this affordance a customer who switches region via the utility
  // dropdown has no persistent visual confirmation of which region they're
  // acting on — the audit flagged this as a UX gap that erodes trust in
  // the "one cluster = one region" contract.
  let identityTitle = resolvedProductName;
  if (scope.tenant) {
    identityTitle = `${identityTitle} · ${scope.tenant}`;
    if (scope.region) {
      identityTitle = `${identityTitle} @ ${scope.region}`;
    }
  }

  const utilities: TopNavigationProps.Utility[] = [
    {
      type: 'menu-dropdown',
      iconName: 'globe',
      ariaLabel: t('topNav.regionAria'),
      text: scope.region ?? t('topNav.regionPlaceholder'),
      items: scope.regions.map((r) => ({ id: r.id, text: r.name })),
      onItemClick: ({ detail }: { detail: { id: string } }) => scope.setRegion(detail.id),
    },
  ];

  // Language switcher lives right after region — same "global scope"
  // affordance grouping. Placed BEFORE search/notifications/profile so
  // it's the first thing an operator lands on when scanning right-to-left.
  if (langUtility) utilities.push(langUtility);

  // Inline `search` slot wins. When it's not provided, fall back to the
  // legacy icon-button that opens the modal command palette.
  if (!search && onSearch) {
    utilities.push({ type: 'button', iconName: 'search', ariaLabel: t('topNav.search'), onClick: onSearch });
  }

  utilities.push({
    type: 'button',
    iconName: 'notification',
    ariaLabel: t('topNav.notifications'),
    badge: false,
  });

  const profileUtility: TopNavigationProps.MenuDropdownUtility = {
    type: 'menu-dropdown',
    iconName: 'user-profile',
    ariaLabel: t('topNav.profileAria'),
    text: auth.user?.name ?? t('topNav.signIn'),
    items: [
      {
        id: 'theme',
        text: theme === 'dark' ? t('topNav.toggleTheme.toLight') : t('topNav.toggleTheme.toDark'),
      },
      { id: 'density', text: t('topNav.toggleDensity') },
      { id: 'signout', text: t('topNav.signOut') },
    ],
    onItemClick: ({ detail }: { detail: { id: string } }) => {
      if (detail.id === 'theme') onToggleTheme?.();
      if (detail.id === 'density') onToggleDensity?.();
      if (detail.id === 'signout') void auth.signOut();
    },
  };
  if (auth.user?.email) profileUtility.description = auth.user.email;
  utilities.push(profileUtility);

  return (
    <TopNavigation
      identity={{ href: '/', title: identityTitle }}
      {...(search ? { search } : {})}
      utilities={utilities}
    />
  );
}
